/**
 * CLEARANCE RESOLVER (MEV5)
 * Mechanism: Systemic Nutrient Disposal
 * Standards: Cunningham (1980), Circadian (2024), Glycogen Sink (2024)
 */

import { V5PhysiologicalState as PhysiologicalState, ClinicalFraction } from '../types';

export const ClearanceResolver = {
    
    /**
     * Calculates the grams of a specific fraction cleared from the blood system per minute.
     * @param state Current physiological state
     * @param fraction The specific nutrient (e.g., glucose, lipids)
     * @param baseRate The specific K_clear for this fraction
     * @param circadianMod Circadian variance multiplier (+/- 20%)
     * @param profile User profile for sensitivity scaling
     */
    compute(
        state: PhysiologicalState, 
        fraction: ClinicalFraction, 
        baseRate: number,
        circadianMod: number,
        profile: any
    ): number {
        const comp = state.fractions[fraction];
        if (comp.blood <= 0) return 0;

        let rate = baseRate;

        // 1. DYNAMIC MODULATION
        // Glycogen Sink, Circadian Rhythm, and Sensitivity apply primarily to the glucose pathway.
        if (fraction === 'glucose' || fraction === 'complex_carbs') {
            
            // A. Profile Sensitivity Scaling
            const sensitivityFactor = profile?.insulin_sensitivity === 'LOW' ? 0.7 : (profile?.insulin_sensitivity === 'HIGH' ? 1.3 : 1.0);
            rate *= sensitivityFactor;

            // B. Apply circadian sine-wave
            rate *= circadianMod;
            
            // C. THE PERSISTENT METABOLIC VACUUM (MEV5.3)
            // If we have a glycogen debt, clearance is accelerated by 3x (The GLUT4 Sink)
            if (state.glycogenDebt > 0 || state.intensity > 0) {
                rate *= 3.0; 
            }
        }

        const flow = comp.blood * rate;
        
        return Math.min(comp.blood, flow);
    }
};
